import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import ManualSelectionsBanner from '../components/ManualSelectionsBanner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import {
  Loader2,
  Plus,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Calendar,
  User,
  FileText,
  Trash2,
  RefreshCw,
  ListTodo,
  CircleDot,
  CheckCircle,
  XCircle,
  Filter,
  ChevronRight,
  Building2,
  Edit2,
  MoreVertical,
  AlarmClock,
  LayoutGrid,
  List,
  Play,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow, isAfter, isBefore, isToday, addHours, addDays } from 'date-fns';
import { toast } from 'sonner';

const PRIORITY_CONFIG = {
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, sortOrder: 0 },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle, sortOrder: 1 },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, sortOrder: 2 },
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock, sortOrder: 3 },
};

const STATUS_CONFIG = {
  todo: { label: 'To Do', color: 'bg-slate-100 text-slate-700', icon: ListTodo, kanbanColor: 'border-slate-300' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: CircleDot, kanbanColor: 'border-blue-400' },
  done: { label: 'Done', color: 'bg-green-100 text-green-700', icon: CheckCircle, kanbanColor: 'border-green-400' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle, kanbanColor: 'border-gray-300' },
};

export default function TasksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my_tasks');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  
  const [users, setUsers] = useState([]);
  const [requirements, setRequirements] = useState([]);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirement_id: 'none',
    assigned_to: 'self',
    due_date: '',
    priority: 'medium',
  });

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      const params = new URLSearchParams();
      params.append('tab', activeTab);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter && priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (assigneeFilter && assigneeFilter !== 'all' && isAdmin) params.append('assigned_to', assigneeFilter);
      if (overdueOnly) params.append('overdue', 'true');
      
      const response = await fetch(`${API_URL}/api/manual-selections/tasks?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, priorityFilter, assigneeFilter, overdueOnly, isAdmin]);

  const loadUsers = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/users/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadRequirements = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/requirements?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequirements(data.requirements || []);
      }
    } catch (error) {
      console.error('Failed to load requirements:', error);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    loadUsers();
    loadRequirements();
  }, []);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    try {
      setSaving(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      const payload = {
        title: formData.title.trim(),
        description: formData.description || null,
        requirement_id: formData.requirement_id === 'none' ? null : formData.requirement_id,
        assigned_to: formData.assigned_to === 'self' ? null : formData.assigned_to,
        due_date: formData.due_date || null,
        priority: formData.priority,
      };
      
      const response = await fetch(`${API_URL}/api/manual-selections/tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Task created');
        setCreateModalOpen(false);
        setFormData({ title: '', description: '', requirement_id: 'none', assigned_to: 'self', due_date: '', priority: 'medium' });
        loadTasks();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create task');
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    
    try {
      setSaving(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      const payload = {
        title: formData.title.trim(),
        description: formData.description || null,
        requirement_id: formData.requirement_id === 'none' ? null : formData.requirement_id,
        assigned_to: formData.assigned_to === 'unassigned' ? null : formData.assigned_to,
        due_date: formData.due_date || null,
        priority: formData.priority,
      };
      
      const response = await fetch(`${API_URL}/api/manual-selections/tasks/${selectedTask.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Task updated');
        setEditModalOpen(false);
        setSelectedTask(null);
        loadTasks();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update task');
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    
    try {
      setSaving(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      const response = await fetch(`${API_URL}/api/manual-selections/tasks/${selectedTask.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Task deleted');
        setDeleteModalOpen(false);
        setSelectedTask(null);
        loadTasks();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete task');
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      const response = await fetch(`${API_URL}/api/manual-selections/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Status changed to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
        loadTasks();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleQuickComplete = async (taskId) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      const response = await fetch(`${API_URL}/api/manual-selections/tasks/${taskId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Task completed!');
        loadTasks();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to complete task');
      }
    } catch (error) {
      console.error('Failed to complete task:', error);
      toast.error('Failed to complete task');
    }
  };

  const handleSnooze = async (taskId, duration) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      
      const response = await fetch(`${API_URL}/api/manual-selections/tasks/${taskId}/snooze?duration=${duration}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const durationLabels = {
          '1h': '1 hour',
          '3h': '3 hours',
          '1d': '1 day',
          'tomorrow_9am': 'tomorrow 9 AM'
        };
        toast.success(`Task snoozed for ${durationLabels[duration] || duration}`);
        loadTasks();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to snooze task');
      }
    } catch (error) {
      console.error('Failed to snooze task:', error);
      toast.error('Failed to snooze task');
    }
  };

  const openEditModal = (task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title || '',
      description: task.description || '',
      requirement_id: task.requirement_id || 'none',
      assigned_to: task.assigned_to || 'unassigned',
      due_date: task.due_date ? task.due_date.slice(0, 16) : '',
      priority: task.priority || 'medium',
    });
    setEditModalOpen(true);
  };

  const openDeleteModal = (task) => {
    setSelectedTask(task);
    setDeleteModalOpen(true);
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = parseISO(dateStr);
      const relative = formatDistanceToNow(date, { addSuffix: true });
      const absolute = format(date, "d MMM yyyy, h:mm a");
      return { relative, absolute };
    } catch {
      return { relative: '-', absolute: '-' };
    }
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'done' || task.status === 'cancelled') return false;
    try {
      return isBefore(parseISO(task.due_date), new Date());
    } catch {
      return false;
    }
  };

  const isDueToday = (task) => {
    if (!task.due_date || task.status === 'done' || task.status === 'cancelled') return false;
    try {
      return isToday(parseISO(task.due_date));
    } catch {
      return false;
    }
  };

  // Group tasks by status for kanban view
  const tasksByStatus = {
    todo: tasks.filter(t => t.status === 'todo'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    done: tasks.filter(t => t.status === 'done'),
  };

  // Task Card Component (reusable for both views)
  const TaskCard = ({ task, compact = false }) => {
    const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
    const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.todo;
    const dueInfo = formatDueDate(task.due_date);
    const taskOverdue = isOverdue(task);
    const taskDueToday = isDueToday(task);
    
    return (
      <Card 
        className={`transition-all hover:shadow-md ${taskOverdue ? 'border-l-4 border-l-red-500' : taskDueToday ? 'border-l-4 border-l-yellow-500' : ''}`}
        data-testid={`task-card-${task.id}`}
      >
        <CardContent className={compact ? "py-3 px-3" : "py-4"}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`font-semibold text-slate-900 ${compact ? 'text-sm' : ''} truncate`}>{task.title}</h3>
                <Badge className={`${priorityConfig.color} text-xs`}>{priorityConfig.label}</Badge>
                {taskOverdue && <Badge className="bg-red-500 text-white text-xs">Overdue</Badge>}
                {taskDueToday && !taskOverdue && <Badge className="bg-yellow-500 text-white text-xs">Due Today</Badge>}
              </div>
              
              {!compact && task.description && (
                <p className="text-sm text-slate-600 mb-2 line-clamp-2">{task.description}</p>
              )}
              
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                {task.assigned_to_name && (
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {task.assigned_to_name}
                  </span>
                )}
                
                {task.due_date && (
                  <span className={`flex items-center gap-1 ${taskOverdue ? 'text-red-600 font-medium' : ''}`}>
                    <Calendar className="w-3 h-3" />
                    <span title={dueInfo.absolute}>{dueInfo.relative}</span>
                  </span>
                )}
                
                {!compact && task.requirement_title && (
                  <Link 
                    to={`/ats/requirements/${task.requirement_id}`}
                    className="flex items-center gap-1 text-purple-600 hover:text-purple-700"
                  >
                    <Building2 className="w-3 h-3" />
                    {task.requirement_title}
                  </Link>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-1 shrink-0">
              {/* Quick Complete Button */}
              {task.status !== 'done' && task.status !== 'cancelled' && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                  onClick={() => handleQuickComplete(task.id)}
                  title="Mark complete"
                  data-testid={`complete-task-${task.id}`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
              )}
              
              {/* Actions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`task-actions-${task.id}`}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* Status Change */}
                  <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'todo')}>
                    <ListTodo className="w-4 h-4 mr-2" /> To Do
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'in_progress')}>
                    <Play className="w-4 h-4 mr-2" /> In Progress
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'done')}>
                    <CheckCircle className="w-4 h-4 mr-2" /> Done
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  {/* Snooze Options */}
                  {task.status !== 'done' && task.status !== 'cancelled' && (
                    <>
                      <DropdownMenuItem onClick={() => handleSnooze(task.id, '1h')}>
                        <AlarmClock className="w-4 h-4 mr-2" /> Snooze +1 hour
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSnooze(task.id, '3h')}>
                        <AlarmClock className="w-4 h-4 mr-2" /> Snooze +3 hours
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSnooze(task.id, '1d')}>
                        <AlarmClock className="w-4 h-4 mr-2" /> Snooze +1 day
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleSnooze(task.id, 'tomorrow_9am')}>
                        <AlarmClock className="w-4 h-4 mr-2" /> Tomorrow 9 AM
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                    </>
                  )}
                  
                  <DropdownMenuItem onClick={() => openEditModal(task)}>
                    <Edit2 className="w-4 h-4 mr-2" /> Edit
                  </DropdownMenuItem>
                  
                  {(isAdmin || task.created_by === user?.id) && (
                    <DropdownMenuItem 
                      onClick={() => openDeleteModal(task)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="tasks-page-title">Tasks & Reminders</h1>
            <p className="text-slate-500 mt-1">Manage your tasks with due dates, priorities, and escalations</p>
          </div>
          <div className="flex gap-2">
            {/* View Mode Toggle */}
            <div className="flex border rounded-lg overflow-hidden">
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'} 
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'kanban' ? 'default' : 'ghost'} 
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('kanban')}
                data-testid="view-kanban-btn"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
            
            <Button variant="outline" onClick={loadTasks} disabled={loading} data-testid="refresh-tasks-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={() => {
              setFormData({ title: '', description: '', requirement_id: 'none', assigned_to: 'self', due_date: '', priority: 'medium' });
              setCreateModalOpen(true);
            }} data-testid="new-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Task
            </Button>
          </div>
        </div>

        {/* Manual Selections Banner */}
        <ManualSelectionsBanner />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="my_tasks" data-testid="tab-my-tasks">
              <User className="w-4 h-4 mr-2" />
              My Tasks
            </TabsTrigger>
            <TabsTrigger value="created_by_me" data-testid="tab-created-by-me">
              <FileText className="w-4 h-4 mr-2" />
              Created by Me
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="all" data-testid="tab-all-tasks">
                <ListTodo className="w-4 h-4 mr-2" />
                All Tasks
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Filters:</span>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="todo">To Do</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[140px]" data-testid="priority-filter">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              
              {isAdmin && (
                <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="assignee-filter">
                    <SelectValue placeholder="Assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Assignees</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Button
                variant={overdueOnly ? 'default' : 'outline'}
                size="sm"
                onClick={() => setOverdueOnly(!overdueOnly)}
                data-testid="overdue-filter-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Overdue Only
              </Button>
              
              <Badge variant="secondary">{tasks.length} tasks</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Tasks Display */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : tasks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ListTodo className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No tasks found</h3>
              <p className="text-slate-500 mt-1">Create a new task to get started</p>
            </CardContent>
          </Card>
        ) : viewMode === 'kanban' ? (
          /* Kanban View */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {['todo', 'in_progress', 'done'].map((status) => {
              const statusConfig = STATUS_CONFIG[status];
              const StatusIcon = statusConfig.icon;
              const statusTasks = tasksByStatus[status] || [];
              
              return (
                <div key={status} className={`border-t-4 ${statusConfig.kanbanColor} rounded-lg bg-white shadow-sm`}>
                  <div className="p-4 border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StatusIcon className="w-5 h-5" />
                        <h3 className="font-semibold">{statusConfig.label}</h3>
                      </div>
                      <Badge variant="secondary">{statusTasks.length}</Badge>
                    </div>
                  </div>
                  <div className="p-3 space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto">
                    {statusTasks.map((task) => (
                      <TaskCard key={task.id} task={task} compact />
                    ))}
                    {statusTasks.length === 0 && (
                      <p className="text-sm text-slate-400 text-center py-4">No tasks</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="space-y-3">
            {tasks.map((task) => (
              <TaskCard key={task.id} task={task} />
            ))}
          </div>
        )}

        {/* Create Task Modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>New Task</DialogTitle>
              <DialogDescription>Create a new task with optional assignment and due date</DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  data-testid="task-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Task title"
                  maxLength={255}
                  required
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  data-testid="task-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Assign To</Label>
                  <Select 
                    value={formData.assigned_to} 
                    onValueChange={(v) => setFormData({ ...formData, assigned_to: v })}
                  >
                    <SelectTrigger data-testid="task-assigned-to-select">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">Myself</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(v) => setFormData({ ...formData, priority: v })}
                  >
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Due Date</Label>
                  <Input
                    data-testid="task-due-date-input"
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Linked Requirement</Label>
                  <Select 
                    value={formData.requirement_id} 
                    onValueChange={(v) => setFormData({ ...formData, requirement_id: v })}
                  >
                    <SelectTrigger data-testid="task-requirement-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {requirements.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.position_title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="create-task-submit-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create Task
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Task Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Task</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleUpdateTask} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  data-testid="edit-task-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Task title"
                  maxLength={255}
                  required
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  data-testid="edit-task-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Assign To</Label>
                  <Select 
                    value={formData.assigned_to} 
                    onValueChange={(v) => setFormData({ ...formData, assigned_to: v })}
                  >
                    <SelectTrigger data-testid="edit-task-assigned-to-select">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(v) => setFormData({ ...formData, priority: v })}
                  >
                    <SelectTrigger data-testid="edit-task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Due Date</Label>
                  <Input
                    data-testid="edit-task-due-date-input"
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Linked Requirement</Label>
                  <Select 
                    value={formData.requirement_id} 
                    onValueChange={(v) => setFormData({ ...formData, requirement_id: v })}
                  >
                    <SelectTrigger data-testid="edit-task-requirement-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {requirements.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.position_title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="edit-task-submit-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Task</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{selectedTask?.title}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="button" 
                variant="destructive" 
                onClick={handleDeleteTask} 
                disabled={saving}
                data-testid="confirm-delete-task-btn"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
